import React from 'react';

/**
 * Config-driven table that renders a normal <table> at >=768px and stacks
 * each row into a label:value card below that (CSS handles the swap via
 * .rtable-desktop / .rtable-cards, see index.css — no resize listeners).
 *
 * columns: [{
 *   key,                    // unique key
 *   label,                  // header text + card row label
 *   render(row, index),     // cell/value content — same output used in both modes
 *   headerRender(),         // optional — overrides the default <th>{label}</th> (e.g. sortable header)
 *   className,              // applied to <th>/<td> in desktop mode
 *   width,                  // applied as <th style={{width}}>
 *   title: true,            // this column becomes the card's title row instead of a label:value row
 *   hideInCard: true,       // skip this column entirely in card mode
 *   titleLeft: true,        // renders inline before the title (e.g. a row checkbox) instead of its own row
 *   titleRight: true,       // renders inline after the title (e.g. status icons) instead of its own row
 *   half: true,             // pairs up with an adjacent `half` column into one shared row instead of a full-width one
 * }]
 */
export default function ResponsiveTable({
  columns, rows, rowKey, onRowClick, rowClassName, rowStyle,
  emptyState = null,
}) {
  if (!rows || rows.length === 0) return emptyState;

  const titleCol = columns.find(c => c.title);
  const leftCol = columns.find(c => c.titleLeft);
  const rightCol = columns.find(c => c.titleRight);
  const bodyCols = columns.filter(c => !c.title && !c.hideInCard && !c.titleLeft && !c.titleRight);

  // Consecutive `half` columns share one row (side by side); everything else stays full-width.
  const cardRowGroups = [];
  for (let i = 0; i < bodyCols.length; i++) {
    const col = bodyCols[i];
    if (col.half && bodyCols[i + 1]?.half) {
      cardRowGroups.push([col, bodyCols[i + 1]]);
      i++;
    } else {
      cardRowGroups.push([col]);
    }
  }

  return (
    <>
      <div className="table-container rtable-desktop">
        <table>
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key} className={col.className} style={col.width ? { width: col.width } : undefined}>
                  {col.headerRender ? col.headerRender() : col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={{ ...(onRowClick ? { cursor: 'pointer' } : {}), ...(rowStyle ? rowStyle(row) : {}) }}
                className={rowClassName ? rowClassName(row) : undefined}
              >
                {columns.map(col => (
                  <td key={col.key} className={col.className}>{col.render(row, i)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rtable-cards">
        {rows.map((row, i) => (
          <div
            key={rowKey(row)}
            className={`rtable-card${onRowClick ? ' clickable' : ''}`}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {(titleCol || leftCol || rightCol) && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                {leftCol && <div style={{ flexShrink: 0, paddingTop: 1 }}>{leftCol.render(row, i)}</div>}
                {titleCol && (
                  <div style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: '.9rem' }}>
                    {titleCol.render(row, i)}
                  </div>
                )}
                {rightCol && <div style={{ flexShrink: 0, display: 'flex', gap: 4 }}>{rightCol.render(row, i)}</div>}
              </div>
            )}
            {cardRowGroups.map((group, gi) => {
              const items = group
                .map(col => ({ col, value: col.render(row, i) }))
                .filter(({ value }) => value !== null && value !== undefined && value !== '');
              if (!items.length) return null;
              if (items.length === 1 && group.length === 1) {
                const { col, value } = items[0];
                return (
                  <div key={col.key} className="rtable-card-row">
                    <span className="rtable-card-label">{col.label}</span>
                    <span className="rtable-card-value">{value}</span>
                  </div>
                );
              }
              return (
                <div key={gi} className="rtable-card-row rtable-card-row-split">
                  {items.map(({ col, value }) => (
                    <div key={col.key} className="rtable-card-split-item">
                      <div className="rtable-card-label">{col.label}</div>
                      <div className="rtable-card-value rtable-card-value-left">{value}</div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}
